import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { disconnectEcho, getEcho } from "@/lib/echo";

/**
 * Subscribes to branch KOT board WebSocket updates via Laravel Reverb.
 * Returns connection state; invalidates kot-tickets query on each push.
 */
export function useKotBoardLive(branchId) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("connecting");

  useEffect(() => {
    if (!branchId) {
      setStatus("offline");
      return undefined;
    }

    const echo = getEcho();
    if (!echo) {
      setStatus("offline");
      return undefined;
    }

    setStatus("connecting");
    const channelName = `kot-board.${branchId}`;
    const channel = echo.private(channelName);

    channel.listen(".kot.updated", () => {
      queryClient.invalidateQueries({ queryKey: ["kot-tickets", branchId] });
    });

    const connector = echo.connector?.pusher;
    if (connector) {
      const onConnected = () => setStatus("live");
      const onDisconnected = () => setStatus("reconnecting");
      const onFailed = () => setStatus("offline");

      connector.connection.bind("connected", onConnected);
      connector.connection.bind("disconnected", onDisconnected);
      connector.connection.bind("failed", onFailed);
      connector.connection.bind("unavailable", onFailed);

      if (connector.connection.state === "connected") {
        setStatus("live");
      }

      return () => {
        connector.connection.unbind("connected", onConnected);
        connector.connection.unbind("disconnected", onDisconnected);
        connector.connection.unbind("failed", onFailed);
        connector.connection.unbind("unavailable", onFailed);
        echo.leave(channelName);
      };
    }

    setStatus("live");

    return () => {
      echo.leave(channelName);
    };
  }, [branchId, queryClient]);

  useEffect(() => () => disconnectEcho(), []);

  return status;
}
